import cn from 'classnames';
import { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';

import Button from 'components/common/Button';
import Heading from 'components/common/Heading';
import ToggleButton from 'components/common/ToggleButton';
import Tooltip from 'components/common/Tooltip';
import Cow from 'components/icons/Cow';
import Fish from 'components/icons/Fish';
import Mushroom from 'components/icons/Mushroom';
import Peanuts from 'components/icons/Peanuts';
import Pork from 'components/icons/Pork';
import Poultry from 'components/icons/Poultry';
import Sesame from 'components/icons/Sesame';
import Shellfish from 'components/icons/Shellfish';
import TreeNuts from 'components/icons/TreeNuts';
import { ProgressBarContext, UserContext } from 'components/signUpFunnel/Contexts';
import { BrandContext, HOME_CHEF } from 'context/BrandContext';
import { brandData } from 'helpers/BrandData';
import { getBrandRoute } from 'helpers/routes.js';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import useAnalyticsEffect from 'hooks/useAnalyticsEffect';
import useBrand from 'hooks/useBrand';
import useFunnelMealPlan from 'hooks/useFunnelMealPlan';
import useMetadata from 'hooks/useMetadata';
import { FAMILY, VEGETARIAN, PESCATARIAN, Statuses } from 'lib/constants';
import * as ErrorLogging from 'services/ErrorLogging';
import {
  getPreferences,
  updatePreferences,
  createEvents,
} from 'services/RequestService/endpoints/signUp';
import { updateStatus } from 'services/RequestService/endpoints/user';
import styles from 'styles/components/signUpFunnel/preferences/PreferencesContainer';

import MenuGallery from './MenuGallery';
import PreferencesFormSkeleton from './PreferencesFormSkeleton';

const IconMap = {
  beef: Cow,
  pork: Pork,
  poultry: Poultry,
  shellfish: Shellfish,
  peanuts: Peanuts,
  'tree nuts': TreeNuts,
  sesame: Sesame,
  fish: Fish,
  mushrooms: Mushroom,
};

const PreferencesAvoidContainer = () => {
  const [preferences, setPreferences] = useState(null);
  const [allergyIds, setAllergyIds] = useState(null);
  const [dietaryPreferenceIds, setDietaryPreferenceIds] = useState(null);
  const { setProgress } = useContext(ProgressBarContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { brand } = useContext(BrandContext);
  const isHomeChef = useBrand(HOME_CHEF);
  const mealPlanSlug = useFunnelMealPlan();
  const { user, setUser } = useContext(UserContext);
  const history = useHistory();
  const isFamilyMealPlan = mealPlanSlug === FAMILY;

  useMetadata({
    title: `Step 2: Preferences | ${brandData(brand)?.displayName}`,
  });

  useAnalyticsEffect(
    ({ events, logEvent }) => {
      if (brand) {
        logEvent(events.signUp[brand].preferencesAvoidances.viewed);
      }
    },
    [brand]
  );

  function handleVegetarianSelected(allergies, response, setPreferences) {
    const updatedAllergies = allergies.map(allergy =>
      ['Beef', 'Poultry', 'Fish', 'Pork', 'Shellfish'].includes(allergy.friendlyName)
        ? { ...allergy, selected: true }
        : allergy
    );
    setPreferences({
      ...response.preferences,
      allergies: updatedAllergies,
    });
  }

  function handlePescatarianSelected(allergies, response, setPreferences) {
    const updatedAllergies = allergies.map(allergy =>
      ['Beef', 'Poultry', 'Pork'].includes(allergy.friendlyName)
        ? { ...allergy, selected: true }
        : allergy
    );
    setPreferences({
      ...response.preferences,
      allergies: updatedAllergies,
    });
  }

  useEffect(() => {
    getPreferences().then(response => {
      if (response.ok) {
        const preferences = response.preferences.dietaryPreferences;
        const allergies = response.preferences.allergies;
        const isVegetarianSelected = preferences.some(
          preference => preference.friendlyName === VEGETARIAN && preference.selected
        );
        const isPescatarianSelected = preferences.some(
          preference => preference.friendlyName === PESCATARIAN && preference.selected
        );

        if (isVegetarianSelected) {
          handleVegetarianSelected(allergies, response, setPreferences);
        } else if (isPescatarianSelected) {
          handlePescatarianSelected(allergies, response, setPreferences);
        } else {
          setPreferences(response.preferences);
        }
      }
    });
  }, []);

  useEffect(() => {
    setProgress({
      percent: '33',
      stepNumber: '2',
      totalSteps: '5',
      nextStep: 'Promos',
    });

    createEvents({ params: { event_name: 'PageView' } }).catch(error =>
      ErrorLogging.captureException(error.message)
    );
  }, []);

  useEffect(() => {
    if (preferences?.allergies) {
      setAllergyIds(
        preferences.allergies
          .filter(allergy => allergy.selected === true)
          .map(allergy => allergy.id)
      );
    }
  }, [preferences?.allergies]);

  useEffect(() => {
    if (preferences?.dietaryPreferences) {
      setDietaryPreferenceIds(
        preferences.dietaryPreferences
          .filter(dietaryPreference => dietaryPreference.selected === true)
          .map(dietaryPreference => dietaryPreference.id)
      );
    }
  }, [preferences?.dietaryPreferences]);

  const handleAllergyClick = ({ friendlyName, selected }) => {
    const newAllergyState = preferences.allergies.map(allergy => {
      if (allergy.friendlyName === friendlyName) {
        return { ...allergy, selected: !selected };
      }
      return allergy;
    });

    setPreferences({ allergies: newAllergyState });
  };

  const handleOnClick = useAnalyticsCallback(
    (allergyIds, dietaryPreferenceIds) => {
      setIsSubmitting(true);
      updatePreferences({ params: { allergyIds, dietaryPreferenceIds } })
        .then(response => {
          setIsSubmitting(false);
          if (response.ok && response.preferences?.dietaryPreferences) {
            updateStatus({
              params: {
                status: isFamilyMealPlan ? Statuses.BOX_PEOPLE_STEP : Statuses.PROMO_STEP,
              },
            });
            setUser(prevUser => ({
              ...prevUser,
              status: isFamilyMealPlan ? Statuses.BOX_PEOPLE_STEP : Statuses.PROMO_STEP,
              dietaryPreferenceNames: response.preferences.dietaryPreferences
                .filter(pref => pref.selected === true)
                .map(pref => pref.friendlyName),
              optIns: response.optIns,
            }));
          }

          if (isFamilyMealPlan) {
            history.push(
              getBrandRoute({ brand: brand, isFamily: isFamilyMealPlan }).boxRecommenderPeople
            );
          } else {
            history.push(getBrandRoute({ brand: brand, isFamily: isFamilyMealPlan }).promo);
          }
        })
        .catch(error => {
          return error;
        });
    },
    ({ logEvent, events }) => {
      const allergies = preferences.allergies
        .filter(allergy => allergy.selected)
        .map(allergy => allergy.friendlyName);

      if (brand) {
        logEvent(events.signUp[brand].preferencesAvoidances.submitted, {
          allergies,
          mealPlanSlug: mealPlanSlug,
        });
      }
    },
    [preferences, brand, mealPlanSlug]
  );

  return (
    <>
      {user && preferences?.allergies ? (
        <>
          <Heading className={'mb-2 px-6 text-center bp2:px-60 bp3:px-80'}>
            Are there any foods you would like to avoid?
          </Heading>
          <div className={cn(styles[brand], styles.preferencesContainer, 'pb-4 pt-2')}>
            <section>
              <Heading
                brandStylesOverrides="h6 text"
                className={cn(
                  {
                    'font-normal': isHomeChef,
                  },
                  'pb-4 text-center'
                )}
              >
                You'll still have access to the full menu.
                <Tooltip
                  ariaId="acct-avoid-profile"
                  containerClass="ml-2"
                  triggerButtonClass={'align-middle bp2:align-text-bottom bp2:mb-0'}
                  position={'bottom'}
                  tooltipClass={cn('max-w-xs', { 'bp2:top-0': isFamilyMealPlan })}
                >
                  <>
                    <h1 className="textMd mb-2">A note about serious food allergies</h1>
                    <p className="textSm">
                      All ingredients are individually packaged, but our central facility is not
                      certified allergen-free. Furthermore, ingredient contents may vary. Please
                      check ingredient packaging for allergens and nutrition facts. If you have
                      serious allergies, please use your best judgment or consult a health
                      professional to decide if our meals are safe for you!
                    </p>
                  </>
                </Tooltip>
              </Heading>
              <div className="grid w-full grid-cols-2 gap-x-1.5 gap-y-2">
                {preferences?.allergies.map(allergy => {
                  const IconComponent = IconMap[allergy.friendlyName.toLowerCase()];
                  return (
                    <ToggleButton
                      key={`column-${allergy.id}`}
                      id={allergy.id}
                      className="relative flex h-14 w-full items-center justify-between rounded px-3"
                      name={allergy.name}
                      selected={allergy.selected}
                      handleClick={() => handleAllergyClick(allergy)}
                      type="avoidance-pref"
                      role="checkbox"
                      aria-checked={allergy.selected}
                      aria-label={allergy.friendlyName}
                    >
                      {allergy.friendlyName}
                      {IconComponent && <IconComponent className="mb-2 h-6 w-6" />}
                    </ToggleButton>
                  );
                })}
              </div>
            </section>
            <Button
              type="submit"
              className="button mx-auto mt-8 w-full"
              onClick={() => handleOnClick(allergyIds, dietaryPreferenceIds)}
              isSubmitting={isSubmitting}
            />
          </div>
          <MenuGallery />
        </>
      ) : (
        <PreferencesFormSkeleton />
      )}
    </>
  );
};

export default PreferencesAvoidContainer;
